import LumaAccessiblePanel from '../../../components/admin/CoworkFormPanel';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { createAfiliacion, Afiliacion, fetchConfigsDomiciliacion } from '../../../services/afiliadosService';
import { fetchClientes, Cliente } from '../../../services/clienteService';
import axiosInstance from '../../../services/axiosConfig';
import {
    X,
    Building2,
    AlertCircle,
    Clock,
    Gift,
    Package,
    Sparkles,
    CheckCircle2
} from 'lucide-react';

interface NuevaAfiliacionModalProps {
    onClose: () => void;
    onCreated: (afiliacion: Afiliacion) => void;
    clienteInicial?: any;
}

const NuevaAfiliacionModal: React.FC<NuevaAfiliacionModalProps> = ({ onClose, onCreated, clienteInicial }) => {
    const [clientes, setClientes] = useState<Array<{ id: number; nombre: string; email: string; telefono?: string; rol?: string }>>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [clienteId, setClienteId] = useState<string>('');
    const [empresaNombre, setEmpresaNombre] = useState<string>('');
    const [nit, setNit] = useState<string>('');
    const [representanteLegal, setRepresentanteLegal] = useState<string>('');
    const [telefonoContacto, setTelefonoContacto] = useState<string>('');
    const [correoContacto, setCorreoContacto] = useState<string>('');
    const [rubro, setRubro] = useState<string>('');
    const [horasAsignadas, setHorasAsignadas] = useState<number>(8);
    const [tipoContrato, setTipoContrato] = useState<'mensual' | 'anual'>('mensual');
    const [tipoCorteCiclo, setTipoCorteCiclo] = useState<'fecha_pago' | 'mes_calendario'>('fecha_pago');

    // Configs dinámicas de Horas de Regalo y Correspondencia
    const [configHorasRegalo, setConfigHorasRegalo] = useState<any | null>(null);
    const [configCorrespondencia, setConfigCorrespondencia] = useState<any | null>(null);
    const [preciosPlan, setPreciosPlan] = useState<any[]>([]);
    const [precioPlanSeleccionado, setPrecioPlanSeleccionado] = useState<number | null>(null);
    const [loadingConfigs, setLoadingConfigs] = useState(false);

    // Cargar configs de Horas de Regalo y Correspondencia
    useEffect(() => {
        let isMounted = true;
        const cargarConfigs = async () => {
            setLoadingConfigs(true);
            try {
                const confs = await fetchConfigsDomiciliacion();
                if (!isMounted) return;
                setConfigHorasRegalo(confs.horasRegalo);
                setConfigCorrespondencia(confs.correspondencia);
                setPreciosPlan(confs.preciosPlan);

                if (confs.horasRegalo && confs.horasRegalo.horas_asignadas) {
                    setHorasAsignadas(confs.horasRegalo.horas_asignadas);
                }

                // Autoseleccionar plan de domicilio comercial si existe
                const planDom = confs.preciosPlan.find((p: any) =>
                    p.servicio?.nombre?.toLowerCase().includes('domicilio') ||
                    p.servicio_nombre?.toLowerCase().includes('domicilio')
                );
                if (planDom) {
                    setPrecioPlanSeleccionado(planDom.id);
                }
            } catch (err) {
                /* Browser diagnostics omitted. */
            } finally {
                if (isMounted) setLoadingConfigs(false);
            }
        };
        cargarConfigs();
        return () => { isMounted = false; };
    }, []);

    // Cargar usuarios
    useEffect(() => {
        let isMounted = true;
        const loadUsers = async () => {
            setLoadingUsers(true);
            try {
                let normalizados: Array<{ id: number; nombre: string; email: string; telefono?: string; rol?: string }> = [];

                // Primero intentar con fetchClientes() estándar del sistema
                const clientesList = await fetchClientes();
                if (Array.isArray(clientesList) && clientesList.length > 0) {
                    normalizados = clientesList
                        .filter(c => c && c.usuario && c.usuario.id)
                        .map(c => {
                            const u = c.usuario!;
                            const nombre = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
                            return {
                                id: u.id,
                                nombre,
                                email: u.email || '',
                                telefono: c.telefono || (u as any).telefono || '',
                                rol: (u as any).rol || u.tipo_nivel_usuario?.nombre || 'Cliente',
                            };
                        });
                } else {
                    // Fallback a /users/
                    const res = await axiosInstance.get('users/');
                    const rawList = Array.isArray(res.data) ? res.data : (res.data?.results || []);
                    if (Array.isArray(rawList)) {
                        normalizados = rawList
                            .filter(u => u && u.id)
                            .map(u => ({
                                id: u.id,
                                nombre: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
                                email: u.email || '',
                                telefono: u.telefono || '',
                                rol: u.tipo_nivel_usuario?.nombre || u.rol || 'Cliente',
                            }));
                    }
                }

                if (!isMounted) return;
                setClientes(normalizados);

                // Si se proporcionó clienteInicial, auto-seleccionarlo
                if (clienteInicial && clienteInicial.id) {
                    const cIdStr = clienteInicial.id.toString();
                    setClienteId(cIdStr);
                    const nom = `${clienteInicial.first_name || ''} ${clienteInicial.last_name || ''}`.trim() || clienteInicial.email;
                    setRepresentanteLegal(nom || '');
                    setCorreoContacto(clienteInicial.email || '');
                    setTelefonoContacto(clienteInicial.telefono || '');
                }
            } catch (err) {
                /* Browser diagnostics omitted. */
                if (isMounted) setClientes([]);
            } finally {
                if (isMounted) setLoadingUsers(false);
            }
        };
        loadUsers();
        return () => { isMounted = false; };
    }, [clienteInicial]);

    const handleSelectUser = (id: string) => {
        setClienteId(id);
        const user = clientes.find((u) => u.id.toString() === id);
        if (user) {
            if (!representanteLegal) {
                setRepresentanteLegal(user.nombre);
            }
            if (!correoContacto) {
                setCorreoContacto(user.email);
            }
            if (!telefonoContacto && user.telefono) {
                setTelefonoContacto(user.telefono);
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!clienteId) {
            setError('Por favor selecciona un cliente titular.');
            return;
        }
        if (!empresaNombre.trim()) {
            setError('Por favor ingresa la razón social o nombre de la empresa.');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const payload: any = {
                cliente: Number(clienteId),
                empresa_nombre: empresaNombre.trim(),
                nit: nit.trim(),
                representante_legal: representanteLegal.trim(),
                telefono_contacto: telefonoContacto.trim(),
                correo_contacto: correoContacto.trim(),
                rubro: rubro.trim(),
                tipo_contrato: tipoContrato,
                tipo_corte_ciclo: tipoCorteCiclo,
                horas_gratuitas_asignadas: horasAsignadas,
                estado: 'en_proceso',
            };

            if (precioPlanSeleccionado) {
                payload.precio_plan_servicio = precioPlanSeleccionado;
            }

            const nueva = await createAfiliacion(payload);
            onCreated(nueva);
            onClose();
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setError(err.response?.data?.detail || 'Error al registrar la domiciliación.');
        } finally {
            setSubmitting(false);
        }
    };

    return createPortal(
        <LumaAccessibleDialog open={true} onClose={onClose} data-luma-overlay="true" className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-md overflow-y-auto flex items-center justify-center p-4">
            <LumaAccessiblePanel data-luma-dialog="true" className="bg-white rounded-3xl shadow-2xl max-w-xl w-full max-h-[92vh] flex flex-col overflow-hidden my-auto border border-gray-100">
                {/* Header fijo */}
                <div className="px-6 py-5 bg-gradient-to-r from-gray-900 to-gray-800 text-white flex items-center justify-between sticky top-0 z-10 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#3564db] rounded-xl flex items-center justify-center shadow">
                            <Building2 className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <LumaAccessibleTitle className="text-base font-black">Nueva Afiliación de Empresa</LumaAccessibleTitle>
                            <p className="text-xs text-gray-400">Inicia el proceso de 7 pasos del diagrama de Miro</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl text-gray-400 hover:text-white transition">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Formulario */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1 custom-scroll"><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>
                    {error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Sincronización Automática con Configs */}
                    <div className="bg-gradient-to-r from-blue-50/80 via-indigo-50/60 to-purple-50/80 border border-blue-200/80 rounded-2xl p-3.5 space-y-2.5">
                        <div className="flex items-center gap-2 text-xs font-black text-blue-950">
                            <Sparkles className="w-4 h-4 text-blue-600" />
                            <span>Beneficios Sincronizados de Domiciliación</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                            <div className="bg-white/80 backdrop-blur-sm p-2.5 rounded-xl border border-blue-100 flex items-start gap-2">
                                <Gift className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                                <div>
                                    <span className="font-bold text-gray-900 block">Horas de Cortesía:</span>
                                    <span className="text-purple-700 font-semibold">{horasAsignadas} hrs mensuales</span>
                                    <span className="text-[10px] text-gray-500 block">(HorasRegaloConfig)</span>
                                </div>
                            </div>
                            <div className="bg-white/80 backdrop-blur-sm p-2.5 rounded-xl border border-blue-100 flex items-start gap-2">
                                <Package className="w-4 h-4 text-[#3564db] flex-shrink-0 mt-0.5" />
                                <div>
                                    <span className="font-bold text-gray-900 block">Correspondencia:</span>
                                    <span className="text-[#3564db] font-semibold">
                                        {configCorrespondencia ? 'Habilitada (Recepción física)' : 'Recepción estándar'}
                                    </span>
                                    <span className="text-[10px] text-gray-500 block truncate max-w-[150px]">
                                        {configCorrespondencia?.descripcion || 'CorrespondenciaConfig'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-700">Cliente Titular en Co Marca *</label>
                        <select
                            value={clienteId}
                            onChange={(e) => handleSelectUser(e.target.value)}
                            required
                            disabled={loadingUsers}
                            className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#3564db] bg-white disabled:bg-gray-100"
                        >
                            <option value="">
                                {loadingUsers ? '-- Cargando clientes... --' : '-- Selecciona un cliente registrado (cualquier rol) --'}
                            </option>
                            {Array.isArray(clientes) && clientes.map((u) => (
                                <option key={u.id} value={u.id}>
                                    {u.nombre} ({u.email}) - Rol: {u.rol || 'Cliente'}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Plan y Tarifa de Domiciliación */}
                    {preciosPlan.length > 0 && (
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-700">Plan / Servicio de Domiciliación</label>
                            <select
                                value={precioPlanSeleccionado || ''}
                                onChange={(e) => setPrecioPlanSeleccionado(e.target.value ? Number(e.target.value) : null)}
                                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#3564db] bg-white"
                            >
                                <option value="">-- Usar plan por defecto (Domicilio Comercial) --</option>
                                {preciosPlan.map((p) => {
                                    const sNombre = p.servicio?.nombre || p.servicio_nombre || 'Servicio';
                                    return (
                                        <option key={p.id} value={p.id}>
                                            {sNombre} — {p.precio_inicial} Bs
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                    )}

                    <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-700">Razón Social o Nombre de la Empresa *</label>
                        <input
                            type="text"
                            value={empresaNombre}
                            onChange={(e) => setEmpresaNombre(e.target.value)}
                            placeholder="Ej: Innovatech Bolivia S.R.L."
                            required
                            className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#3564db]"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-700">NIT (Opcional):</label>
                            <input
                                type="text"
                                value={nit}
                                onChange={(e) => setNit(e.target.value)}
                                placeholder="Ej: 1029384756"
                                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#3564db]"
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-700">Representante Legal:</label>
                            <input
                                type="text"
                                value={representanteLegal}
                                onChange={(e) => setRepresentanteLegal(e.target.value)}
                                placeholder="Nombre completo"
                                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#3564db]"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-700">Teléfono de Contacto:</label>
                            <input
                                type="text"
                                value={telefonoContacto}
                                onChange={(e) => setTelefonoContacto(e.target.value)}
                                placeholder="Ej: +591 77712345"
                                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#3564db]"
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-700">Correo Electrónico:</label>
                            <input
                                type="email"
                                value={correoContacto}
                                onChange={(e) => setCorreoContacto(e.target.value)}
                                placeholder="empresa@correo.com"
                                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#3564db]"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-700">Rubro o Actividad:</label>
                            <input
                                type="text"
                                value={rubro}
                                onChange={(e) => setRubro(e.target.value)}
                                placeholder="Ej: Consultoría, Software, Minería..."
                                className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#3564db]"
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-bold text-gray-700">Horas Mensuales de Cortesía:</label>
                            <div className="relative">
                                <input
                                    type="number"
                                    min={1}
                                    max={100}
                                    value={horasAsignadas}
                                    onChange={(e) => setHorasAsignadas(Number(e.target.value))}
                                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#3564db]"
                                />
                                <span className="absolute right-3 top-2.5 text-xs text-gray-400 font-bold">hrs/mes</span>
                            </div>
                        </div>
                    </div>

                    <div className="p-3.5 bg-purple-50/70 border border-purple-200 rounded-2xl space-y-3">
                        <div className="text-xs font-black text-purple-950 flex items-center gap-2">
                            <span>🏢 Modalidad de Oficina Virtual & Ciclo de Horas</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <label className="text-[11px] font-bold text-purple-900">Duración del Contrato:</label>
                                <select
                                    value={tipoContrato}
                                    onChange={(e) => setTipoContrato(e.target.value as 'mensual' | 'anual')}
                                    className="w-full px-3 py-2 border border-purple-300 rounded-xl text-xs bg-white focus:ring-2 focus:ring-purple-500"
                                >
                                    <option value="mensual">Mensual (1 mes renovable)</option>
                                    <option value="anual">Anual (12 meses continuos)</option>
                                </select>
                            </div>

                            <div className="space-y-1">
                                <label className="text-[11px] font-bold text-purple-900">Corte de 8 Horas Mensuales:</label>
                                <select
                                    value={tipoCorteCiclo}
                                    onChange={(e) => setTipoCorteCiclo(e.target.value as 'fecha_pago' | 'mes_calendario')}
                                    className="w-full px-3 py-2 border border-purple-300 rounded-xl text-xs bg-white focus:ring-2 focus:ring-purple-500"
                                >
                                    <option value="fecha_pago">Fecha de Pago (Aniversario mensual)</option>
                                    <option value="mes_calendario">Principio de Mes (1 al 30/31)</option>
                                </select>
                            </div>
                        </div>
                        <p className="text-[11px] text-purple-800 italic">
                            * Las 8 horas son mensuales y <strong>no acumulables</strong> entre períodos.
                        </p>
                    </div>

                    {/* Footer con botones */}
                    <div className="pt-4 flex items-center justify-end gap-3 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2.5 text-xs font-bold text-gray-600 hover:bg-gray-100 rounded-xl transition"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-5 py-2.5 bg-[#3564db] hover:bg-[#006ca8] text-white text-xs font-bold rounded-xl transition shadow flex items-center gap-2"
                        >
                            {submitting && <Clock className="w-4 h-4 animate-spin" />}
                            <span>Registrar e Iniciar Flujo</span>
                        </button>
                    </div>
                </form>
            </LumaAccessiblePanel>
        </LumaAccessibleDialog>,
        document.body
    );
};

export default NuevaAfiliacionModal;

